import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { facilitiesAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import {
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  PlusIcon,
  XMarkIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

interface FacilityFormData {
  name: string;
  licenseNumber: string;
  type: string;
  capacity: number;
  currentOccupancy: number;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  contact: {
    name: string;
    phone: string;
    email: string;
  };
  amenities: string[];
  costRange: string;
  minCost: number;
  maxCost: number;
  acceptsInsurance: boolean;
  specialties: string[];
  notes: string;
  isActive: boolean;
}

const FACILITY_TYPES = [
  'Assisted Living',
  'Nursing Home',
  'Memory Care',
  'Independent Living',
  'Skilled Nursing',
  'Rehabilitation Center',
  'Adult Day Care',
  'Home Care',
  'Other'
];

const COMMON_AMENITIES = [
  '24/7 Nursing Care',
  'Physical Therapy',
  'Occupational Therapy',
  'Speech Therapy',
  'Memory Care Services',
  'Medication Management',
  'Meals Included',
  'Transportation Services',
  'Social Activities',
  'Exercise Programs',
  'Beauty/Barber Services',
  'Laundry Services',
  'Housekeeping',
  'WiFi',
  'Cable TV',
  'Private Rooms',
  'Semi-Private Rooms',
  'Outdoor Spaces',
  'Pet Friendly',
  'Respite Care'
];

const COMMON_SPECIALTIES = [
  'Alzheimer\'s Care',
  'Dementia Care',
  'Parkinson\'s Care',
  'Stroke Recovery',
  'Cardiac Care',
  'Diabetes Management',
  'Wound Care',
  'Post-Surgical Care',
  'Rehabilitation',
  'Palliative Care',
  'Hospice Care',
  'Mental Health',
  'Substance Abuse Recovery'
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const FacilityForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToast();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<FacilityFormData>({
    name: '',
    licenseNumber: '',
    type: '',
    capacity: 0,
    currentOccupancy: 0,
    address: {
      street: '',
      city: '',
      state: '',
      zip: ''
    },
    contact: {
      name: '',
      phone: '',
      email: ''
    },
    amenities: [],
    costRange: '',
    minCost: 0,
    maxCost: 0,
    acceptsInsurance: false,
    specialties: [],
    notes: '',
    isActive: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newAmenity, setNewAmenity] = useState('');
  const [newSpecialty, setNewSpecialty] = useState('');

  // Fetch facility data if editing
  const { data: facilityData, isLoading: facilityLoading } = useQuery({
    queryKey: ['facility', id],
    queryFn: () => facilitiesAPI.getFacility(id!),
    enabled: isEditMode
  });

  // Populate form data when editing
  useEffect(() => {
    if (isEditMode && facilityData?.data?.data?.facility) {
      const facility = facilityData.data.data.facility;
      setFormData({
        name: facility.name || '',
        licenseNumber: facility.licenseNumber || '',
        type: facility.type || '',
        capacity: facility.capacity || 0,
        currentOccupancy: facility.currentOccupancy || 0,
        address: {
          street: facility.address?.street || '',
          city: facility.address?.city || '',
          state: facility.address?.state || '',
          zip: facility.address?.zip || ''
        },
        contact: {
          name: facility.contact?.name || '',
          phone: facility.contact?.phone || '',
          email: facility.contact?.email || ''
        },
        amenities: facility.amenities || [],
        costRange: facility.costRange || '',
        minCost: facility.minCost || 0,
        maxCost: facility.maxCost || 0,
        acceptsInsurance: facility.acceptsInsurance || false,
        specialties: facility.specialties || [],
        notes: facility.notes || '',
        isActive: facility.isActive !== false
      });
    }
  }, [facilityData, isEditMode]);

  // Create facility mutation
  const createFacilityMutation = useMutation({
    mutationFn: (data: FacilityFormData) => facilitiesAPI.createFacility(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facilities'] });
      queryClient.invalidateQueries({ queryKey: ['facilities-dashboard'] });
      showSuccess('Facility created successfully!');
      navigate('/facilities');
    },
    onError: (error: any) => {
      showError(error.response?.data?.message || 'Failed to create facility');
    }
  });

  // Update facility mutation
  const updateFacilityMutation = useMutation({
    mutationFn: (data: FacilityFormData) => facilitiesAPI.updateFacility(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facilities'] });
      queryClient.invalidateQueries({ queryKey: ['facility', id] });
      queryClient.invalidateQueries({ queryKey: ['facilities-dashboard'] });
      showSuccess('Facility updated successfully!');
      navigate('/facilities');
    },
    onError: (error: any) => {
      showError(error.response?.data?.message || 'Failed to update facility');
    }
  });

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Facility name is required';
    if (!formData.licenseNumber.trim()) newErrors.licenseNumber = 'License number is required';
    if (!formData.type) newErrors.type = 'Facility type is required';
    if (!formData.capacity || formData.capacity < 1) newErrors.capacity = 'Capacity must be at least 1';
    if (formData.currentOccupancy < 0) newErrors.currentOccupancy = 'Current occupancy cannot be negative';
    if (formData.currentOccupancy > formData.capacity) newErrors.currentOccupancy = 'Current occupancy cannot exceed capacity';
    
    if (!formData.address.street.trim()) newErrors['address.street'] = 'Street address is required';
    if (!formData.address.city.trim()) newErrors['address.city'] = 'City is required';
    if (!formData.address.state.trim()) newErrors['address.state'] = 'State is required';
    if (!formData.address.zip.trim()) newErrors['address.zip'] = 'ZIP code is required';
    else if (!/^\d{5}(-\d{4})?$/.test(formData.address.zip)) newErrors['address.zip'] = 'Please enter a valid ZIP code';

    if (!formData.contact.name.trim()) newErrors['contact.name'] = 'Contact name is required';
    if (!formData.contact.phone.trim()) newErrors['contact.phone'] = 'Contact phone is required';
    if (formData.contact.email && !/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(formData.contact.email)) {
      newErrors['contact.email'] = 'Please enter a valid email address';
    }

    if (!formData.costRange.trim()) newErrors.costRange = 'Cost range is required';
    if (formData.minCost < 0) newErrors.minCost = 'Minimum cost cannot be negative';
    if (formData.maxCost < 0) newErrors.maxCost = 'Maximum cost cannot be negative';
    if (formData.minCost > formData.maxCost && formData.maxCost > 0) {
      newErrors.maxCost = 'Maximum cost must be greater than minimum cost';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Please fix the errors below');
      return;
    }

    if (isEditMode) {
      updateFacilityMutation.mutate(formData);
    } else {
      createFacilityMutation.mutate(formData);
    }
  };

  // Handle input changes
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        return {
          ...prev,
          [parent]: {
            ...(prev as any)[parent],
            [child]: value
          }
        };
      }
      return { ...prev, [field]: value };
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle amenity management
  const addAmenity = (amenity: string) => {
    if (amenity && !formData.amenities.includes(amenity)) {
      setFormData(prev => ({
        ...prev,
        amenities: [...prev.amenities, amenity]
      }));
    }
    setNewAmenity('');
  };

  const removeAmenity = (amenity: string) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter(a => a !== amenity)
    }));
  };

  // Handle specialty management
  const addSpecialty = (specialty: string) => {
    if (specialty && !formData.specialties.includes(specialty)) {
      setFormData(prev => ({
        ...prev,
        specialties: [...prev.specialties, specialty]
      }));
    }
    setNewSpecialty('');
  };

  const removeSpecialty = (specialty: string) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.filter(s => s !== specialty)
    }));
  };

  if (isEditMode && facilityLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/facilities')}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Facilities
          </button>
          <h1 className="text-3xl font-bold text-gray-900 text-left">
            {isEditMode ? 'Edit Facility' : 'Add New Facility'}
          </h1>
          <p className="mt-2 text-gray-600">
            {isEditMode ? 'Update facility information' : 'Enter facility details to add to the system'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white shadow-card rounded-lg p-6">
            <div className="flex items-center mb-6">
              <BuildingOfficeIcon className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">Basic Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Facility Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter facility name"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  License Number *
                </label>
                <input
                  type="text"
                  value={formData.licenseNumber}
                  onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.licenseNumber ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter license number"
                />
                {errors.licenseNumber && <p className="mt-1 text-sm text-red-600">{errors.licenseNumber}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Facility Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.type ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select facility type</option>
                  {FACILITY_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {errors.type && <p className="mt-1 text-sm text-red-600">{errors.type}</p>}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                  Active facility
                </label>
              </div>
            </div>
          </div>

          {/* Capacity Information */}
          <div className="bg-white shadow-card rounded-lg p-6">
            <div className="flex items-center mb-6">
              <UserGroupIcon className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">Capacity Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Capacity *
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.capacity}
                  onChange={(e) => handleInputChange('capacity', parseInt(e.target.value) || 0)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.capacity ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter total capacity"
                />
                {errors.capacity && <p className="mt-1 text-sm text-red-600">{errors.capacity}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Occupancy
                </label>
                <input
                  type="number"
                  min="0"
                  max={formData.capacity}
                  value={formData.currentOccupancy}
                  onChange={(e) => handleInputChange('currentOccupancy', parseInt(e.target.value) || 0)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.currentOccupancy ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter current occupancy"
                />
                {errors.currentOccupancy && <p className="mt-1 text-sm text-red-600">{errors.currentOccupancy}</p>}
              </div>
            </div>

            {formData.capacity > 0 && (
              <div className="mt-4 p-4 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-800">
                  Available Beds: {Math.max(0, formData.capacity - formData.currentOccupancy)} 
                  ({Math.round((Math.max(0, formData.capacity - formData.currentOccupancy) / formData.capacity) * 100)}% availability)
                </p>
              </div>
            )}
          </div>

          {/* Address Information */}
          <div className="bg-white shadow-card rounded-lg p-6">
            <div className="flex items-center mb-6">
              <MapPinIcon className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">Address Information</h2>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Street Address *
                </label>
                <input
                  type="text"
                  value={formData.address.street}
                  onChange={(e) => handleInputChange('address.street', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors['address.street'] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter street address"
                />
                {errors['address.street'] && <p className="mt-1 text-sm text-red-600">{errors['address.street']}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => handleInputChange('address.city', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                      errors['address.city'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter city"
                  />
                  {errors['address.city'] && <p className="mt-1 text-sm text-red-600">{errors['address.city']}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State *
                  </label>
                  <select
                    value={formData.address.state}
                    onChange={(e) => handleInputChange('address.state', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                      errors['address.state'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select state</option>
                    {US_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                  {errors['address.state'] && <p className="mt-1 text-sm text-red-600">{errors['address.state']}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    value={formData.address.zip}
                    onChange={(e) => handleInputChange('address.zip', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                      errors['address.zip'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="12345"
                  />
                  {errors['address.zip'] && <p className="mt-1 text-sm text-red-600">{errors['address.zip']}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white shadow-card rounded-lg p-6">
            <div className="flex items-center mb-6">
              <PhoneIcon className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">Contact Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Name *
                </label>
                <input
                  type="text"
                  value={formData.contact.name}
                  onChange={(e) => handleInputChange('contact.name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors['contact.name'] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter contact name"
                />
                {errors['contact.name'] && <p className="mt-1 text-sm text-red-600">{errors['contact.name']}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.contact.phone}
                  onChange={(e) => handleInputChange('contact.phone', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors['contact.phone'] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="(555) 123-4567"
                />
                {errors['contact.phone'] && <p className="mt-1 text-sm text-red-600">{errors['contact.phone']}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.contact.email}
                  onChange={(e) => handleInputChange('contact.email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors['contact.email'] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="contact@facility.com"
                />
                {errors['contact.email'] && <p className="mt-1 text-sm text-red-600">{errors['contact.email']}</p>}
              </div>
            </div>
          </div>

          {/* Cost Information */}
          <div className="bg-white shadow-card rounded-lg p-6">
            <div className="flex items-center mb-6">
              <CurrencyDollarIcon className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">Cost Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cost Range *
                </label>
                <input
                  type="text"
                  value={formData.costRange}
                  onChange={(e) => handleInputChange('costRange', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.costRange ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="e.g., $3,000 - $5,000/month"
                />
                {errors.costRange && <p className="mt-1 text-sm text-red-600">{errors.costRange}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Cost ($)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.minCost}
                  onChange={(e) => handleInputChange('minCost', parseInt(e.target.value) || 0)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.minCost ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="3000"
                />
                {errors.minCost && <p className="mt-1 text-sm text-red-600">{errors.minCost}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Cost ($)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.maxCost}
                  onChange={(e) => handleInputChange('maxCost', parseInt(e.target.value) || 0)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.maxCost ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="5000"
                />
                {errors.maxCost && <p className="mt-1 text-sm text-red-600">{errors.maxCost}</p>}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="acceptsInsurance"
                  checked={formData.acceptsInsurance}
                  onChange={(e) => handleInputChange('acceptsInsurance', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="acceptsInsurance" className="ml-2 block text-sm text-gray-900">
                  Accepts Insurance
                </label>
              </div>
            </div>
          </div>

          {/* Amenities */}
          <div className="bg-white shadow-card rounded-lg p-6">
            <div className="flex items-center mb-6">
              <ClipboardDocumentListIcon className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">Amenities</h2>
            </div>

            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {COMMON_AMENITIES.map(amenity => (
                  <button
                    key={amenity}
                    type="button"
                    onClick={() => addAmenity(amenity)}
                    disabled={formData.amenities.includes(amenity)}
                    className={`px-3 py-1 text-sm rounded-full border ${
                      formData.amenities.includes(amenity)
                        ? 'bg-blue-100 text-blue-800 border-blue-200 cursor-not-allowed'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200'
                    }`}
                  >
                    {amenity}
                  </button>
                ))}
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={newAmenity}
                  onChange={(e) => setNewAmenity(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAmenity(newAmenity))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add custom amenity"
                />
                <button
                  type="button"
                  onClick={() => addAmenity(newAmenity)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              </div>

              {formData.amenities.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Selected Amenities:</h4>
                  <div className="flex flex-wrap gap-2">
                    {formData.amenities.map(amenity => (
                      <span
                        key={amenity}
                        className="inline-flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full"
                      >
                        {amenity}
                        <button
                          type="button"
                          onClick={() => removeAmenity(amenity)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Specialties */}
          <div className="bg-white shadow-card rounded-lg p-6">
            <div className="flex items-center mb-6">
              <ClipboardDocumentListIcon className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">Specialties</h2>
            </div>

            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {COMMON_SPECIALTIES.map(specialty => (
                  <button
                    key={specialty}
                    type="button"
                    onClick={() => addSpecialty(specialty)}
                    disabled={formData.specialties.includes(specialty)}
                    className={`px-3 py-1 text-sm rounded-full border ${
                      formData.specialties.includes(specialty)
                        ? 'bg-green-100 text-green-800 border-green-200 cursor-not-allowed'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-green-50 hover:text-green-700 hover:border-green-200'
                    }`}
                  >
                    {specialty}
                  </button>
                ))}
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={newSpecialty}
                  onChange={(e) => setNewSpecialty(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialty(newSpecialty))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add custom specialty"
                />
                <button
                  type="button"
                  onClick={() => addSpecialty(newSpecialty)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              </div>

              {formData.specialties.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Selected Specialties:</h4>
                  <div className="flex flex-wrap gap-2">
                    {formData.specialties.map(specialty => (
                      <span
                        key={specialty}
                        className="inline-flex items-center px-3 py-1 text-sm bg-green-100 text-green-800 rounded-full"
                      >
                        {specialty}
                        <button
                          type="button"
                          onClick={() => removeSpecialty(specialty)}
                          className="ml-2 text-green-600 hover:text-green-800"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Additional Notes */}
          <div className="bg-white shadow-card rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Additional Notes</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter any additional notes about the facility..."
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pb-8">
            <button
              type="button"
              onClick={() => navigate('/facilities')}
              className="px-6 py-2 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createFacilityMutation.isPending || updateFacilityMutation.isPending}
              className="px-6 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createFacilityMutation.isPending || updateFacilityMutation.isPending ? (
                <>
                  <LoadingSpinner size="sm" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditMode ? 'Update Facility' : 'Create Facility'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FacilityForm;
